/**
 * NotificationsPage  --  Full notification center
 * 
 * Features:
 * - Grouped notifications by time (Today, Yesterday, This Week, Earlier)
 * - Filter tabs: All, Unread, Forks, Challenges, Social
 * - Click-through navigation to relevant project/challenge/user
 * - Mark individual or all as read
 * - Infinite scroll with pagination
 * - Empty states per filter
 * - Responsive: full page on mobile, constrained on desktop
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, GitFork, Star, MessageCircle, Users, Target, Zap,
  TrendingUp, Check, CheckCheck, Trash2, AlertCircle, Package,
  Filter, ArrowLeft, Settings, ChevronRight, Eye, Sparkles,
  Heart, Award, BookOpen, Flame, Compass, FolderTree
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';

// ============================================================================
// NOTIFICATION TYPE CONFIG  --  Maps each notification type to icon, color, label
// ============================================================================

const NOTIF_CONFIG = {
  project_forked: {
    icon: GitFork,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    ring: 'ring-indigo-500/20',
    label: 'Fork',
    category: 'forks',
  },
  cross_zone_fork: {
    icon: Compass,
    color: 'text-fuchsia-400',
    bg: 'bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10',
    ring: 'ring-fuchsia-500/20',
    label: 'Cross-Zone',
    category: 'forks',
    isSpecial: true,
    renderExtra: (notif) => {
      const extraData = notif.extraData || notif.extra_data || {};
      const { original_zone_label, new_zone_label } = extraData;
      if (!original_zone_label || !new_zone_label) return null;
      return (
        <div className="flex items-center gap-1.5 mt-1.5 text-xs">
          <span className="px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-400">{original_zone_label}</span>
          <span className="text-zinc-600">→</span>
          <span className="px-2 py-0.5 rounded-md bg-fuchsia-500/20 text-fuchsia-400">{new_zone_label}</span>
        </div>
      );
    }
  },
  challenge_response: {
    icon: Target,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    ring: 'ring-emerald-500/20',
    label: 'Challenge',
    category: 'challenges',
  },
  challenge_created: {
    icon: Sparkles,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    label: 'Challenge',
    category: 'challenges',
  },
  collaboration_request: {
    icon: Users,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    ring: 'ring-pink-500/20',
    label: 'Collab',
    category: 'social',
  },
  project_comment: {
    icon: MessageCircle,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    ring: 'ring-cyan-500/20',
    label: 'Comment',
    category: 'social',
  },
  project_starred: {
    icon: Star,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    label: 'Star',
    category: 'social',
  },
  project_liked: {
    icon: Heart,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    ring: 'ring-rose-500/20',
    label: 'Like',
    category: 'social',
  },
  mention: {
    icon: Zap,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    ring: 'ring-purple-500/20',
    label: 'Mention',
    category: 'social',
  },
  new_follower: {
    icon: Users,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    ring: 'ring-pink-500/20',
    label: 'Follow',
    category: 'social',
  },
  project_curated: {
    icon: FolderTree,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    label: 'Curated',
    category: 'social',
    renderExtra: (notif) => {
      const extraData = notif.extraData || notif.extra_data || {};
      const count = extraData.total_projects_in_branch;
      if (!count) return null;
      return (
        <div className="text-xs text-zinc-500 mt-1">
          {count} project{count !== 1 ? 's' : ''} in collection
        </div>
      );
    }
  },
  milestone: {
    icon: Award,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    label: 'Milestone',
    category: 'social',
  },
  peer_review: {
    icon: BookOpen,
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
    ring: 'ring-teal-500/20',
    label: 'Review',
    category: 'social',
  },
  trending: {
    icon: Flame,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    ring: 'ring-orange-500/20',
    label: 'Trending',
    category: 'social',
  },
  system: {
    icon: AlertCircle,
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    ring: 'ring-zinc-500/20',
    label: 'System',
    category: 'social',
  },
};

const DEFAULT_CONFIG = {
  icon: Bell,
  color: 'text-zinc-400',
  bg: 'bg-zinc-500/10',
  ring: 'ring-zinc-500/20',
  label: 'Notification',
  category: 'social',
};

// ============================================================================
// FILTER TABS
// ============================================================================

const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'unread', label: 'Unread', icon: Eye },
  { id: 'forks', label: 'Forks', icon: GitFork },
  { id: 'challenges', label: 'Challenges', icon: Target },
  { id: 'social', label: 'Social', icon: Users },
];

// ============================================================================
// TIME HELPERS
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getDateGroup = (dateStr) => {
  if (!dateStr) return 'Earlier';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Check if actually today
    if (now.toDateString() === date.toDateString()) return 'Today';
    return 'Yesterday';
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 30) return 'This Month';
  return 'Earlier';
};

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

// ============================================================================
// SINGLE NOTIFICATION ROW
// ============================================================================

const NotificationRow = ({ notification, onClickNotif, onMarkRead, onDelete, isCompact }) => {
  const config = NOTIF_CONFIG[notification.type] || DEFAULT_CONFIG;
  const Icon = config.icon;
  const [isHovered, setIsHovered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();
    setIsExiting(true);
    setTimeout(() => onDelete(notification.id), 200);
  };

  const handleMarkRead = (e) => {
    e.stopPropagation();
    onMarkRead(notification.id);
  };

  return (
    <div
      onClick={() => onClickNotif(notification)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group relative flex items-start gap-3 px-4 py-3.5 cursor-pointer transition-all duration-200',
        'hover:bg-zinc-800/40',
        !notification.isRead && 'bg-purple-500/[0.03]',
        isExiting && 'opacity-0 -translate-x-4 h-0 py-0 overflow-hidden'
      )}
    >
      {/* Unread indicator  --  left edge line */}
      {!notification.isRead && (
        <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-purple-500" />
      )}

      {/* Icon */}
      <div className={cn(
        'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5',
        'ring-1 transition-all duration-200',
        config.bg, config.ring,
        !notification.isRead && 'shadow-sm'
      )}>
        <Icon className={cn('w-4 h-4', config.color)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className={cn(
              'text-[13px] leading-relaxed',
              notification.isRead ? 'text-zinc-400' : 'text-zinc-200'
            )}>
              {/* If we have a related user, bold their name */}
              {notification.relatedUser && (
                <span className={cn(
                  'font-semibold',
                  notification.isRead ? 'text-zinc-300' : 'text-white'
                )}>
                  {notification.relatedUser.username}
                </span>
              )}{' '}
              {notification.title}
            </p>

            {notification.message && (
              <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2 leading-relaxed">
                {notification.message}
              </p>
            )}

            {/* Related project chip */}
            {notification.relatedProject && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <div className="w-4 h-4 rounded bg-zinc-800 flex items-center justify-center">
                  <Package className="w-2.5 h-2.5 text-zinc-500" />
                </div>
                <span className="text-[11px] text-zinc-500 truncate max-w-[200px]">
                  {notification.relatedProject.name}
                </span>
              </div>
            )}

            {/* Type-specific extra content (cross-zone badges, curated counts, etc.) */}
            {config.renderExtra && config.renderExtra(notification)}
          </div>

          {/* Time + actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Hover actions */}
            <div className={cn(
              'flex items-center gap-0.5 transition-opacity duration-150',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}>
              {!notification.isRead && (
                <button
                  onClick={handleMarkRead}
                  className="p-1 rounded-md hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300 transition-colors"
                  title="Mark as read"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-1 rounded-md hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-colors"
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            <span className={cn(
              'text-[11px] tabular-nums whitespace-nowrap',
              notification.isRead ? 'text-zinc-600' : 'text-zinc-500'
            )}>
              {timeAgo(notification.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// GROUPED NOTIFICATION SECTION
// ============================================================================

const NotificationGroup = ({ title, notifications, onClickNotif, onMarkRead, onDelete }) => {
  if (!notifications.length) return null;

  return (
    <div>
      <div className="sticky top-0 z-10 px-4 py-2 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/30">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </span>
      </div>
      <div className="divide-y divide-zinc-800/30">
        {notifications.map((notif) => (
          <NotificationRow
            key={notif.id}
            notification={notif}
            onClickNotif={onClickNotif}
            onMarkRead={onMarkRead}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// EMPTY STATE
// ============================================================================

const EmptyState = ({ filter }) => {
  const states = {
    all: {
      icon: Bell,
      title: 'No notifications yet',
      message: 'When someone forks your project, responds to your challenge, or follows you, it\'ll show up here.',
    },
    unread: {
      icon: CheckCheck,
      title: 'All caught up',
      message: 'You\'ve read all your notifications. Nice work staying on top of things.',
    },
    forks: {
      icon: GitFork,
      title: 'No fork notifications',
      message: 'When someone builds on your work, you\'ll see it here. Share your projects to get the lineage flowing.',
    },
    challenges: {
      icon: Target,
      title: 'No challenge activity',
      message: 'Post a challenge on one of your projects to get help from the community.',
    },
    social: {
      icon: Users,
      title: 'No social notifications',
      message: 'Comments, follows, and mentions will appear here as your network grows.',
    },
  };

  const state = states[filter] || states.all;
  const Icon = state.icon;

  return (
    <div className="flex flex-col items-center justify-center py-20 px-6">
      <div className="w-16 h-16 rounded-2xl bg-zinc-800/30 flex items-center justify-center mb-4 ring-1 ring-zinc-800/50">
        <Icon className="w-7 h-7 text-zinc-600" />
      </div>
      <h3 className="text-base font-semibold text-zinc-300 mb-1.5">
        {state.title}
      </h3>
      <p className="text-sm text-zinc-500 text-center max-w-sm leading-relaxed">
        {state.message}
      </p>
    </div>
  );
};

// ============================================================================
// LOADING SKELETONS
// ============================================================================

const NotificationSkeleton = () => (
  <div className="flex items-start gap-3 px-4 py-3.5 animate-pulse">
    <div className="w-9 h-9 rounded-xl bg-zinc-800/50 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 bg-zinc-800/50 rounded-md w-3/4" />
      <div className="h-3 bg-zinc-800/30 rounded-md w-1/2" />
    </div>
    <div className="h-3 bg-zinc-800/30 rounded-md w-8" />
  </div>
);

// ============================================================================
// NOTIFICATIONS PAGE (main export)
// ============================================================================

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const scrollRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // ---- Fetch notifications ----
  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = {
        page: pageNum,
        per_page: 20,
      };
      if (activeFilter === 'unread') {
        params.unread_only = 'true';
      }

      const res = await api.get('/notifications', { params });
      const data = res.data;
      const newNotifs = data.notifications || [];

      if (append) {
        setNotifications(prev => [...prev, ...newNotifs]);
      } else {
        setNotifications(newNotifs);
      }

      setTotalCount(data.total || 0);
      setHasMore(data.hasNext || false);
      setPage(pageNum);
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeFilter]);

  // ---- Fetch unread count ----
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count || 0);
    } catch (e) {
      // Non-critical
    }
  }, []);

  // ---- Initial load + filter change ----
  useEffect(() => {
    fetchNotifications(1, false);
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // ---- Infinite scroll observer ----
  useEffect(() => {
    if (!loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchNotifications(page + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loadingMore, loading, page, fetchNotifications]);

  // ---- Filter notifications by category ----
  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'all' || activeFilter === 'unread') return notifications;

    return notifications.filter(n => {
      const config = NOTIF_CONFIG[n.type] || DEFAULT_CONFIG;
      return config.category === activeFilter;
    });
  }, [notifications, activeFilter]);

  // ---- Group notifications by date ----
  const groupedNotifications = useMemo(() => {
    const groups = {};
    filteredNotifications.forEach(n => {
      const group = getDateGroup(n.createdAt);
      if (!groups[group]) groups[group] = [];
      groups[group].push(n);
    });

    return GROUP_ORDER
      .filter(g => groups[g]?.length > 0)
      .map(g => ({ title: g, notifications: groups[g] }));
  }, [filteredNotifications]);

  // ---- Click notification -> navigate + mark read ----
  const handleClickNotification = async (notif) => {
    // Mark as read
    if (!notif.isRead) {
      try {
        await api.post(`/notifications/${notif.id}/read`);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (e) {
        // Non-critical
      }
    }

    // Navigate to the right place
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    } else if (notif.relatedProjectId) {
      navigate(`/dashboard?project=${notif.relatedProjectId}`);
    } else if (notif.relatedUserId) {
      navigate(`/profile/${notif.relatedUserId}`);
    }
  };

  // ---- Mark single as read ----
  const handleMarkRead = async (notifId) => {
    try {
      await api.post(`/notifications/${notifId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notifId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  // ---- Mark all as read ----
  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read', {});
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all read:', e);
    }
  };

  // ---- Delete single ----
  const handleDelete = async (notifId) => {
    try {
      await api.delete(`/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      setTotalCount(prev => prev - 1);
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="max-w-2xl mx-auto">
        {/* ---- Header ---- */}
        <div className="sticky top-0 md:top-16 z-20 bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800/50">
          {/* Title bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="md:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-zinc-800/50 text-zinc-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-white">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-xs text-zinc-500">
                    {unreadCount} unread
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Mark all read</span>
                </button>
              )}
              <button
                onClick={() => navigate('/settings?tab=notifications')}
                className="p-1.5 rounded-lg hover:bg-zinc-800/50 text-zinc-500 hover:text-zinc-300 transition-colors"
                title="Notification settings"
              >
                <Settings className="w-4.5 h-4.5" />
              </button>
            </div>
          </div>

          {/* Filter tabs  --  horizontally scrollable on mobile */}
          <div className="flex items-center gap-1 px-4 pb-2.5 overflow-x-auto scrollbar-hide">
            {FILTER_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeFilter === tab.id;

              // Count for the tab
              let count = null;
              if (tab.id === 'unread') count = unreadCount;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-150',
                    isActive
                      ? 'bg-purple-500/15 text-purple-400 ring-1 ring-purple-500/20'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                  {count > 0 && (
                    <span className={cn(
                      'min-w-[16px] h-4 flex items-center justify-center px-1 text-[10px] font-bold rounded-full',
                      isActive ? 'bg-purple-500/30 text-purple-300' : 'bg-zinc-800 text-zinc-400'
                    )}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---- Notification list ---- */}
        <div ref={scrollRef}>
          {loading ? (
            <div className="divide-y divide-zinc-800/30">
              {Array.from({ length: 8 }).map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <EmptyState filter={activeFilter} />
          ) : (
            <>
              {groupedNotifications.map((group) => (
                <NotificationGroup
                  key={group.title}
                  title={group.title}
                  notifications={group.notifications}
                  onClickNotif={handleClickNotification}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                />
              ))}

              {/* Infinite scroll sentinel */}
              <div ref={loadMoreRef} className="py-4">
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {!hasMore && filteredNotifications.length > 0 && (
                  <p className="text-center text-xs text-zinc-600 py-4">
                    That's everything
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;